import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Beaker, FlaskConical, Microscope, BarChart, ArrowRight, CheckCircle, Clock, Dna, Layers, Shield, LineChart, Droplets, Leaf, Brain, HeartPulse, Sparkles, Pill, LayoutGrid, ListChecks } from 'lucide-react';
export function MicrobiotaResetProgram() {
  const phases = [{
    title: 'Phase 1: Reset & Disrupt',
    description: 'This initial phase focuses on reducing pathogenic load, weakening biofilms, and beginning to restore mucosal barriers.',
    icon: <Layers className="text-blue-600" size={24} data-id="element-1703" data-mp="1kfqlzfcmr2v5" />,
    color: 'bg-blue-100',
    tools: [{
      name: 'N-Acetylcysteine (NAC)',
      dosage: '600–1200 mg/day',
      function: 'Disrupts biofilms and improves antioxidant status'
    }, {
      name: 'Tomato phytonutrients',
      dosage: '',
      function: 'Provide antimicrobial and prebiotic effects'
    }, {
      name: 'Bacillus laterosporus',
      dosage: '',
      function: 'Spore-forming probiotic that colonizes and breaks down biofilm matrices'
    }]
  }, {
    title: 'Phase 2: Rebuild & Recolonize',
    description: 'The second phase supports recolonization of beneficial bacteria, restores microbial diversity, and strengthens the intestinal lining.',
    icon: <Droplets className="text-blue-600" size={24} data-id="element-1704" data-mp="11cn874psq2fp" />,
    color: 'bg-blue-100',
    tools: [{
      name: 'Multi-strain probiotics',
      dosage: '',
      function: 'Including Bifidobacterium infantis and Lactobacillus species'
    }, {
      name: 'Prebiotics',
      dosage: '',
      function: 'Including resistant starch and various fibers'
    }, {
      name: 'Nutrient cofactors',
      dosage: '',
      function: 'B vitamins, selenium, magnesium'
    }]
  }, {
    title: 'Phase 3: Sustain & Personalize',
    description: 'The final phase builds resilience, maintains microbiome balance, and prevents relapse into dysbiosis.',
    icon: <Leaf className="text-blue-600" size={24} data-id="element-1705" data-mp="3izpynnom9vhy" />,
    color: 'bg-blue-100',
    tools: [{
      name: 'Personalized diet protocols',
      dosage: '',
      function: 'Fiber, polyphenols'
    }, {
      name: 'Lifestyle interventions',
      dosage: '',
      function: 'Circadian rhythm optimization, stress management'
    }, {
      name: 'Long-term maintenance probiotic blends',
      dosage: '',
      function: 'Customized for individual needs'
    }]
  }];
  const validationMethods = [{
    title: 'Microbiome Diversity',
    description: '16S rRNA sequencing to measure changes in microbial populations and diversity indices',
    icon: <Dna className="text-teal-600" size={24} data-id="element-1706" data-mp="u56i9b7pbiw9" />
  }, {
    title: 'Inflammatory Markers',
    description: 'Measurement of CRP, IL-6, TNF-α, PKR to track systemic and local inflammation',
    icon: <LineChart className="text-teal-600" size={24} data-id="element-1707" data-mp="k78vks5hpu5r" />
  }, {
    title: 'Barrier Function',
    description: 'LPS levels and permeability assays to assess gut barrier integrity',
    icon: <Shield className="text-teal-600" size={24} data-id="element-1708" data-mp="agnxr6xn4tnw" />
  }, {
    title: 'Patient Outcomes',
    description: 'Validated assessments of cognition, mood, digestion, energy levels',
    icon: <BarChart className="text-teal-600" size={24} data-id="element-1709" data-mp="1h0yaqoh1xxh" />
  }];
  const differentiators = [{
    title: 'Biofilm-Targeted Approach',
    description: 'Most probiotics fail because biofilms protect pathogens. This program directly addresses that barrier to effectiveness.',
    icon: <Microscope className="text-navy-600" size={24} data-id="element-1710" data-mp="3smslpiav6wgl" />
  }, {
    title: 'Synergistic Multi-Phase System',
    description: 'Combines disruption → recolonization → sustainability, not just adding probiotics to an unbalanced system.',
    icon: <LayoutGrid className="text-navy-600" size={24} data-id="element-1711" data-mp="uvbr27pflkud" />
  }, {
    title: 'Rapid Results',
    description: 'While microbiome change often takes months, the Reset Protocol aims for measurable shifts within 3–4 weeks.',
    icon: <Clock className="text-navy-600" size={24} data-id="element-1712" data-mp="g54fmbxud236" />
  }, {
    title: 'Evidence-Based Validation',
    description: 'Built on rigorous scientific research with multiple biomarkers to validate effectiveness.',
    icon: <ListChecks className="text-navy-600" size={24} data-id="element-1713" data-mp="73n1v07wpchn" />
  }];
  const applications = [{
    title: 'Post-Antibiotic Recovery',
    description: 'Accelerates microbiome recovery after antibiotic treatment',
    icon: <Pill className="text-blue-600" size={24} data-id="element-1714" data-mp="1wiben2keswg7" />
  }, {
    title: 'Digestive Disorders',
    description: 'Addresses dysbiosis associated with IBS, IBD, and other GI conditions',
    icon: <HeartPulse className="text-blue-600" size={24} data-id="element-1715" data-mp="290nixnf6lkzi" />
  }, {
    title: 'Neurological Support',
    description: 'Targets gut-brain axis to support cognitive and mood health',
    icon: <Brain className="text-blue-600" size={24} data-id="element-1716" data-mp="2iqgskivrjq14" />
  }, {
    title: 'Immune Modulation',
    description: 'Helps balance immune response and reduce inappropriate inflammation',
    icon: <Shield className="text-blue-600" size={24} data-id="element-1717" data-mp="1q1t6il36wwmi" />
  }];
  return <div className="flex flex-col min-h-screen bg-white" data-id="element-1718" data-mp="1q3w2rmuxrlzo">
      <Header data-id="element-1719" data-mp="1v0croj9ym4dt" />
      <main className="flex-grow" data-id="element-1720" data-mp="1uzkxncw0gepn">
        {/* Hero Section */}
        <section className="relative w-full bg-gray-50" data-id="element-1721" data-mp="200im277m64zl">
          <div className="absolute inset-0 overflow-hidden" data-id="element-1722" data-mp="1zxw5ji2qwf58">
            <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1576086213369-97a306d36557?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80')",
            backgroundBlendMode: 'overlay'
          }} data-id="element-1723" data-mp="36fj9foikpeuk" />
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-teal-600/20" data-id="element-1724" data-mp="1kd1gkahgb8tu" />
          </div>
          <div className="container mx-auto px-4 py-16 md:py-24 relative z-10" data-id="element-1725" data-mp="1gaems417zdu0">
            <div className="max-w-3xl" data-id="element-1726" data-mp="smwrvzmnrzmt">
              <h1 className="text-3xl md:text-5xl font-bold text-gray-800 mb-4" data-id="element-1727" data-mp="nntxra87vdtm">
                Microbiota Reset Program
              </h1>
              <div className="w-20 h-1 bg-blue-600 mb-6" data-id="element-1728" data-mp="11fryoxrry3ep"></div>
              <p className="text-lg md:text-xl text-gray-700 mb-8" data-id="element-1729" data-mp="wnqf0qsa1aen">
                A comprehensive, evidence-based protocol to restore microbial
                balance, disrupt harmful biofilms, and strengthen gut barrier
                integrity.
              </p>
              <div className="flex flex-wrap gap-4" data-id="element-1730" data-mp="4v9iivlfx78a">
                <a href="#how-it-works" className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 shadow-sm" data-id="element-1731" data-mp="3qxi6b0vsz8mg">
                  How It Works
                </a>
                <a href="/contact" className="px-6 py-3 bg-white text-blue-600 border border-blue-600 rounded-md hover:bg-gray-50 transition-colors duration-200" data-id="element-1732" data-mp="3lzgftuw1s70y">
                  Practitioner Inquiries
                </a>
              </div>
            </div>
          </div>
        </section>
        {/* Purpose Section */}
        <section className="py-16 bg-white" data-id="element-1733" data-mp="3njv84nb0mub">
          <div className="container mx-auto px-4" data-id="element-1734" data-mp="8jr5g9mqgki2">
            <div className="max-w-3xl mx-auto" data-id="element-1735" data-mp="8mn11rb3yx3c">
              <div className="flex items-center mb-6" data-id="element-1736" data-mp="2obey3suprqv2">
                <div className="bg-blue-100 p-3 rounded-full mr-4" data-id="element-1737" data-mp="2oe17ly160gfx">
                  <Beaker className="text-blue-600" size={24} data-id="element-1738" data-mp="2tcchg8rgmqy5" />
                </div>
                <h2 className="text-3xl font-bold text-gray-800" data-id="element-1739" data-mp="2ala1pe0bod6l">Purpose</h2>
              </div>
              <p className="text-gray-600 mb-6" data-id="element-1740" data-mp="25k434zjzdvp5">
                The Microbiota Reset Program is designed to restore microbial
                balance, disrupt harmful biofilms, and strengthen gut barrier
                integrity — faster and more effectively than conventional
                probiotic-only approaches.
              </p>
              <p className="text-gray-600 mb-6" data-id="element-1741" data-mp="2tcubyzwl7yv7">
                Developed by CalMiR scientists and validated through the Ukweli
                Institute's certification process, this protocol represents a
                breakthrough in microbiome intervention by addressing the root
                causes of dysbiosis rather than simply adding probiotics to an
                unbalanced system.
              </p>
              <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-blue-600" data-id="element-1742" data-mp="37aj1il1lnu6l">
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1743" data-mp="378oduyyy1nyt">
                  Key Targets
                </h3>
                <ul className="space-y-2" data-id="element-1744" data-mp="yqoemq209asm">
                  <li className="flex items-start" data-id="element-1745" data-mp="yrr9f92g7xdt">
                    <CheckCircle className="text-blue-600 mr-2 mt-1 flex-shrink-0" size={18} data-id="element-1746" data-mp="12st7vnojk5eq" />
                    <span data-id="element-1747" data-mp="17sxpjh16r66j">
                      Disruption of pathogenic biofilms that protect harmful
                      bacteria
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1748" data-mp="1hoiuno1jwmki">
                    <CheckCircle className="text-blue-600 mr-2 mt-1 flex-shrink-0" size={18} data-id="element-1749" data-mp="1mgsk72yr7km9" />
                    <span data-id="element-1750" data-mp="1qhcezzvuuc58">
                      Recolonization with beneficial microbes to restore
                      diversity
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1751" data-mp="20h3d0bjmj9a2">
                    <CheckCircle className="text-blue-600 mr-2 mt-1 flex-shrink-0" size={18} data-id="element-1752" data-mp="q403p5m9rxj4" />
                    <span data-id="element-1753" data-mp="v3mn5wsoya0d">Strengthening of intestinal barrier function</span>
                  </li>
                  <li className="flex items-start" data-id="element-1754" data-mp="z351g9q0bmu8">
                    <CheckCircle className="text-blue-600 mr-2 mt-1 flex-shrink-0" size={18} data-id="element-1755" data-mp="13vya38oljfbl" />
                    <span data-id="element-1756" data-mp="18wlz5ot9lm36">
                      Reduction of inflammation and immune dysregulation
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>
        {/* How It Works Section */}
        <section id="how-it-works" className="py-16 bg-gray-50" data-id="element-1757" data-mp="1msziz4l9tzkt">
          <div className="container mx-auto px-4" data-id="element-1758" data-mp="3kkk5yqbygoyu">
            <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1759" data-mp="3klv9qq5ehwpy">
              <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-1760" data-mp="3omowvhb0r7hs">
                How It Works
              </h2>
              <div className="w-20 h-1 bg-blue-600 mx-auto mb-6" data-id="element-1761" data-mp="3h3pgzlmrny36"></div>
              <p className="text-gray-600" data-id="element-1762" data-mp="3cb61gj4iqsvq">
                The program is structured into three distinct phases, each
                addressing a key step in resetting the gut microbiome with
                targeted interventions.
              </p>
            </div>
            <div className="space-y-12 max-w-4xl mx-auto" data-id="element-1763" data-mp="3vmc3v6yxuly">
              {phases.map((phase, index) => <div key={index} className="bg-white rounded-lg shadow-sm overflow-hidden" data-id="element-1764" data-mp="3v25npckmpolb" data-mpi={index}>
                  <div className="p-6 border-b border-gray-100" data-id="element-1765" data-mp="3qyr4ye5b2u46">
                    <div className="flex items-center mb-4" data-id="element-1766" data-mp="3m1r20c3r67ha">
                      <div className={`${phase.color} p-3 rounded-full mr-4`} data-id="element-1767" data-mp="3m3k73mypwjn6">
                        {phase.icon}
                      </div>
                      <h3 className="text-2xl font-bold text-gray-800" data-id="element-1768" data-mp="2alsu4bkb9rhn">
                        {phase.title}
                      </h3>
                    </div>
                    <p className="text-gray-600 mb-4" data-id="element-1769" data-mp="2c1epdpz7auts">{phase.description}</p>
                  </div>
                  <div className="bg-gray-50 p-6" data-id="element-1770" data-mp="232jmonvd82v8">
                    <h4 className="text-lg font-bold text-gray-800 mb-4" data-id="element-1771" data-mp="231ror6eapt1s">
                      Tools & Interventions
                    </h4>
                    <div className="space-y-4" data-id="element-1772" data-mp="275zijhzm1z6v">
                      {phase.tools.map((tool, idx) => <div key={idx} className="flex items-start" data-id="element-1773" data-mp="1y3gh92bh3umq" data-mpi={idx}>
                          <CheckCircle className="text-blue-600 mr-3 mt-1 flex-shrink-0" size={18} data-id="element-1774" data-mp="1y4rk8zx3gycs" />
                          <div data-id="element-1775" data-mp="1oe6nzj4w85x1">
                            <p className="font-medium text-gray-800" data-id="element-1776" data-mp="1obao3p9hj75y">
                              {tool.name}{' '}
                              {tool.dosage && <span className="text-gray-500 font-normal" data-id="element-1777" data-mp="1fcfjuiq0475a">
                                  ({tool.dosage})
                                </span>}
                            </p>
                            <p className="text-gray-600 text-sm" data-id="element-1778" data-mp="quf4twcioiq3">
                              {tool.function}
                            </p>
                          </div>
                        </div>)}
                    </div>
                  </div>
                </div>)}
            </div>
            <div className="max-w-3xl mx-auto mt-12 text-center" data-id="element-1779" data-mp="19rgc5ntjmux7">
              <p className="text-gray-600 italic" data-id="element-1780" data-mp="19p3wxif66ynu">
                "The sequential approach is critical — simply adding probiotics
                without first addressing biofilms and creating a hospitable
                environment significantly reduces effectiveness."
              </p>
              <p className="text-gray-500 mt-2" data-id="element-1781" data-mp="1xeg46cqxswni">— CalMiR Research Team</p>
            </div>
          </div>
        </section>
        {/* Measurements & Validation Section */}
        <section className="py-16 bg-white" data-id="element-1782" data-mp="3mwv4zyp2tnh0">
          <div className="container mx-auto px-4" data-id="element-1783" data-mp="3rwr7tum1f8uz">
            <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1784" data-mp="3rvg41usle13v">
              <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-1785" data-mp="hjvstebwelb">
                Measurements & Validation
              </h2>
              <div className="w-20 h-1 bg-blue-600 mx-auto mb-6" data-id="element-1786" data-mp="4idk3nvonveq"></div>
              <p className="text-gray-600" data-id="element-1787" data-mp="33zbvktypi2u2">
                The Microbiota Reset Program isn't just theoretical — it's built
                for evidence-based validation with multiple biomarkers to track
                progress and outcomes.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-1788" data-mp="3hyjrndzkouu1">
              {validationMethods.map((method, index) => <div key={index} className="bg-gray-50 p-6 rounded-lg" data-id="element-1789" data-mp="s87uzzwfz9wc" data-mpi={index}>
                  <div className="flex items-start" data-id="element-1790" data-mp="x82ng7vv87ji">
                    <div className="bg-teal-100 p-3 rounded-full mr-4 flex-shrink-0" data-id="element-1791" data-mp="x6hqkbm2wcih">
                      {method.icon}
                    </div>
                    <div data-id="element-1792" data-mp="12co8wnqd2gxi">
                      <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1793" data-mp="12ehgq6cpn773">
                        {method.title}
                      </h3>
                      <p className="text-gray-600" data-id="element-1794" data-mp="xk3gnqn3sbw3">{method.description}</p>
                    </div>
                  </div>
                </div>)}
            </div>
            <div className="max-w-3xl mx-auto mt-12" data-id="element-1795" data-mp="on2xpzisz601">
              <div className="bg-blue-50 p-6 rounded-lg border border-blue-100" data-id="element-1796" data-mp="jm5gbp5mafzl">
                <h3 className="text-xl font-bold text-gray-800 mb-4 text-center" data-id="element-1797" data-mp="enc1ntelgixu">
                  Clinical Validation
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1798" data-mp="anbnm9veq9rb">
                  The Microbiota Reset Program has undergone clinical validation
                  through a series of studies:
                </p>
                <ul className="space-y-3" data-id="element-1799" data-mp="251cmuvuvcyrx">
                  <li className="flex items-start" data-id="element-1800" data-mp="24y6y5hq5jywq">
                    <div className="bg-blue-100 rounded-full p-1 mr-3 mt-1" data-id="element-1801" data-mp="2ckq58sbwdm8">
                      <CheckCircle className="text-blue-600" size={14} data-id="element-1802" data-mp="74cjfr2vcxg2" />
                    </div>
                    <p className="text-gray-600" data-id="element-1803" data-mp="3pilpw5khrc51">
                      <span className="font-medium" data-id="element-1804" data-mp="3pfz9rhjjbob7">Pilot Study (2021):</span>{' '}
                      87% of participants showed significant improvements in
                      microbial diversity after 6 weeks
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-1805" data-mp="q1vuf08w0q54">
                    <div className="bg-blue-100 rounded-full p-1 mr-3 mt-1" data-id="element-1806" data-mp="q0krt3r6hofl">
                      <CheckCircle className="text-blue-600" size={14} data-id="element-1807" data-mp="c2w9a2kl2tdp" />
                    </div>
                    <p className="text-gray-600" data-id="element-1808" data-mp="h0flyy1muy3j">
                      <span className="font-medium" data-id="element-1809" data-mp="2wsvbrwiv0a8x">
                        Comparative Trial (2022):
                      </span>{' '}
                      3.2x greater reduction in inflammatory markers compared to
                      probiotic-only interventions
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-1810" data-mp="2p8u6ercugbt2">
                    <div className="bg-blue-100 rounded-full p-1 mr-3 mt-1" data-id="element-1811" data-mp="2pc86tu1y6axv">
                      <CheckCircle className="text-blue-600" size={14} data-id="element-1812" data-mp="338vhezmtozm9" />
                    </div>
                    <p className="text-gray-600" data-id="element-1813" data-mp="2ybd5d0die4a1">
                      <span className="font-medium" data-id="element-1814" data-mp="2akg6ao6tb1g7">Ongoing Research:</span>{' '}
                      Current studies are examining long-term outcomes and
                      applications for specific health conditions
                    </p>
                  </li>
                </ul>
                <div className="mt-4 text-center" data-id="element-1815" data-mp="2fkc8qizv2ktn">
                  <a href="/research-hub/publications" className="inline-flex items-center text-blue-600 hover:text-blue-700 transition-colors duration-200 font-medium" data-id="element-1816" data-mp="2fihqjcfz56t9">
                    View research publications
                    <ArrowRight size={16} className="ml-1" data-id="element-1817" data-mp="11d560adcjrkr" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Why It's Different Section */}
        <section className="py-16 bg-gray-50" data-id="element-1818" data-mp="1b8oonv2wgtrk">
          <div className="container mx-auto px-4" data-id="element-1819" data-mp="1g9cmp0pjqava">
            <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1820" data-mp="1gaxlx3mt2hzh">
              <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-1821" data-mp="1kbpv5wvogmvr">
                Why It's Different
              </h2>
              <div className="w-20 h-1 bg-blue-600 mx-auto mb-6" data-id="element-1822" data-mp="1p8q024gsjjla"></div>
              <p className="text-gray-600" data-id="element-1823" data-mp="1u07su58nudv6">
                The Microbiota Reset Program represents a paradigm shift in how
                we approach microbiome health, offering several key advantages
                over conventional approaches.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-1824" data-mp="28093udin31od">
              {differentiators.map((item, index) => <div key={index} className="bg-white p-6 rounded-lg shadow-sm" data-id="element-1825" data-mp="3sjmae4290c18" data-mpi={index}>
                  <div className="flex items-start" data-id="element-1826" data-mp="3ogg795afn0yz">
                    <div className="bg-navy-50 p-3 rounded-full mr-4 flex-shrink-0" data-id="element-1827" data-mp="3oj2f7618jihq">
                      {item.icon}
                    </div>
                    <div data-id="element-1828" data-mp="3ejbh6udgulcw">
                      <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1829" data-mp="3ek3eqaqmit5t">
                        {item.title}
                      </h3>
                      <p className="text-gray-600" data-id="element-1830" data-mp="g2z5kuqkbjqr">{item.description}</p>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </section>
        {/* Applications Section */}
        <section className="py-16 bg-white" data-id="element-1831" data-mp="1b8gzh8m5620">
          <div className="container mx-auto px-4" data-id="element-1832" data-mp="1a5qvbj477n5">
            <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1833" data-mp="yspcgg7lnvz1">
              <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-1834" data-mp="u0xgfq5ocy1i">
                Clinical Applications
              </h2>
              <div className="w-20 h-1 bg-blue-600 mx-auto mb-6" data-id="element-1835" data-mp="1bofq6a00x1ox"></div>
              <p className="text-gray-600" data-id="element-1836" data-mp="16p2uczr6cka7">
                The Microbiota Reset Program has shown promising results in
                multiple areas of health.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto" data-id="element-1837" data-mp="xoegc51s6jxb">
              {applications.map((app, index) => <div key={index} className="bg-gray-50 p-6 rounded-lg text-center" data-id="element-1838" data-mp="1udm4lktsb74x" data-mpi={index}>
                  <div className="bg-blue-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4" data-id="element-1839" data-mp="1phnhe0k6zv63">
                    {app.icon}
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2" data-id="element-1840" data-mp="1gmqzmjmepa6m">
                    {app.title}
                  </h3>
                  <p className="text-gray-600 text-sm" data-id="element-1841" data-mp="2d6zql0fis38e">{app.description}</p>
                </div>)}
            </div>
          </div>
        </section>
        {/* Ecosystem Integration Section */}
        <section className="py-16 bg-gray-50" data-id="element-1842" data-mp="3w09lfafg7ytk">
          <div className="container mx-auto px-4" data-id="element-1843" data-mp="3w12jzo497i0m">
            <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1844" data-mp="3wgslgawn84z">
              <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-1845" data-mp="3v5p7hldg2ee">
                Part of the CalMiR Ecosystem
              </h2>
              <div className="w-20 h-1 bg-blue-600 mx-auto mb-6" data-id="element-1846" data-mp="duo6fq9wda2r"></div>
              <p className="text-gray-600" data-id="element-1847" data-mp="duei074i3az6">
                The Microbiota Reset Program exemplifies our integrated approach
                to microbiome science.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto" data-id="element-1848" data-mp="nl9ctzvtbiw8">
              <div className="bg-white p-6 rounded-lg shadow-sm" data-id="element-1849" data-mp="rkros65nojmx">
                <div className="bg-teal-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4" data-id="element-1850" data-mp="wlo37dnmvo6l">
                  <Microscope className="text-teal-600" size={24} data-id="element-1851" data-mp="11jfv47oa3aas" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1852" data-mp="2xmucxc0chzr1">
                  Researched by CalMiR
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1853" data-mp="2splpavwfh3ie">
                  Developed through rigorous scientific research at the
                  California Institute for Microbiome Research.
                </p>
                <a href="/research" className="inline-flex items-center text-teal-600 hover:text-teal-700 transition-colors duration-200" data-id="element-1854" data-mp="32kvuttm1bt0x">
                  Learn about our research
                  <ArrowRight size={16} className="ml-1" data-id="element-1855" data-mp="3qc18q38f0b8l" />
                </a>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm" data-id="element-1856" data-mp="3q9oqdoyqvyv0">
                <div className="bg-blue-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4" data-id="element-1857" data-mp="3ldhkfw6cmnd2">
                  <Beaker className="text-blue-600" size={24} data-id="element-1858" data-mp="1vy9zv5xwtve7" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1859" data-mp="1r0i1bt2rfb11">
                  Developed by CalBiome
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1860" data-mp="6j8us9dxifj6">
                  Formulated into practical protocols and products through our
                  product innovation division.
                </p>
                <a href="/calbiome" className="inline-flex items-center text-blue-600 hover:text-blue-700 transition-colors duration-200" data-id="element-1861" data-mp="3sumrhbwltv3y">
                  Explore CalBiome
                  <ArrowRight size={16} className="ml-1" data-id="element-1862" data-mp="3ovf3fho5pwsv" />
                </a>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm" data-id="element-1863" data-mp="39y4sc9ffesxo">
                <div className="bg-emerald-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4" data-id="element-1864" data-mp="39zzflue66x4x">
                  <CheckCircle className="text-emerald-600" size={24} data-id="element-1865" data-mp="1868gszt9dj2w" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1866" data-mp="1d5cr5trx5mut">
                  Certified by Ukweli
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1867" data-mp="398r8yy3zkcb2">
                  Independently validated through the Ukweli Institute's
                  rigorous certification process.
                </p>
                <a href="/certification" className="inline-flex items-center text-emerald-600 hover:text-emerald-700 transition-colors duration-200" data-id="element-1868" data-mp="3079u1j2begny">
                  Learn about certification
                  <ArrowRight size={16} className="ml-1" data-id="element-1869" data-mp="2qawsy04fx0j6" />
                </a>
              </div>
            </div>
          </div>
        </section>
        {/* Access Section */}
        <section className="py-16 bg-blue-600 text-white" data-id="element-1870" data-mp="2hi38loa4zayg">
          <div className="container mx-auto px-4" data-id="element-1871" data-mp="2ciguzdqebsp4">
            <div className="max-w-3xl mx-auto text-center" data-id="element-1872" data-mp="2cjs0bhzhp8ic">
              <h2 className="text-3xl font-bold mb-4" data-id="element-1873" data-mp="27jnk7t2hufsn">Access the Program</h2>
              <p className="text-blue-100 mb-8" data-id="element-1874" data-mp="22k11j43wc7cg">
                The Microbiota Reset Program is available through licensed
                healthcare practitioners and as part of our global health
                initiatives.
              </p>
              <div className="flex flex-wrap justify-center gap-4" data-id="element-1875" data-mp="1xdvy8t83jeuk">
                <a href="/contact" className="px-6 py-3 bg-white text-blue-600 rounded-md hover:bg-gray-100 transition-colors duration-200 shadow-sm" data-id="element-1876" data-mp="22dqqp17ischq">
                  For Healthcare Providers
                </a>
                <a href="/about/afya-program" className="px-6 py-3 bg-blue-700 text-white border border-blue-400 rounded-md hover:bg-blue-800 transition-colors duration-200" data-id="element-1877" data-mp="19nrvsp6n9l55">
                  Global Health Initiatives
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer data-id="element-1878" data-mp="qyll9pg07zuf" />
    </div>;
}